<script setup lang="ts">
import type { GeneratedMediaProjection } from '@skiphr/contracts'
import { Check, Copy, ExternalLink, Film, ImageIcon } from 'lucide-vue-next'
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { firebaseClient } from '@/firebase'
import { useAuthStore } from '@/stores/auth'

type Filter = 'all' | 'image' | 'video'
const media = ref<readonly GeneratedMediaProjection[]>([])
const filter = ref<Filter>('all')
const copied = ref('')
const auth = useAuthStore()
let unsubscribe: (() => void) | undefined

const shown = computed(() => media.value.filter((item) => filter.value === 'all' || item.kind === filter.value))
const stamp = (value: number) => new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

onMounted(() => {
  const uid = auth.user?.uid
  if (!uid) return
  unsubscribe = onSnapshot(query(collection(firebaseClient.firestore, 'generatedMedia', uid, 'items'), orderBy('createdAt', 'desc'), limit(200)),
    (snapshot) => { media.value = snapshot.docs.map((item) => item.data() as GeneratedMediaProjection) })
})
onBeforeUnmount(() => unsubscribe?.())

async function copy(item: GeneratedMediaProjection): Promise<void> {
  try { await navigator.clipboard.writeText(item.url); copied.value = item.id; setTimeout(() => { if (copied.value === item.id) copied.value = '' }, 1_500) }
  catch { window.open(item.url, '_blank', 'noopener') }
}
</script>

<template><div class="section-page">
  <header class="section-page__header"><div><span class="eyebrow">Media</span><h1>Media library</h1>
    <p>Images and videos your Manager and agents created. Review them here before they are published. Each file has a private link that anyone you share it with can open.</p></div></header>
  <nav class="comms-tabs" role="tablist">
    <button v-for="item in ([['all', 'All'], ['image', 'Images'], ['video', 'Videos']] as const)" :key="item[0]" type="button" role="tab"
      :aria-selected="filter === item[0]" :class="{ active: filter === item[0] }" @click="filter = item[0]">{{ item[1] }}</button>
  </nav>
  <section v-if="shown.length" class="media-grid">
    <article v-for="item in shown" :key="item.id" class="media-card">
      <div class="media-card__frame" :style="{ aspectRatio: item.aspectRatio.replace(':', ' / ') }">
        <img v-if="item.kind === 'image'" :src="item.url" :alt="item.prompt" loading="lazy" />
        <video v-else :src="item.url" controls preload="metadata" playsinline />
      </div>
      <div class="media-card__body">
        <div class="media-card__meta"><span><ImageIcon v-if="item.kind === 'image'" :size="13" /><Film v-else :size="13" /> {{ item.generatedBy }}</span>
          <small>{{ item.credits }} credits · {{ stamp(item.createdAt) }}</small></div>
        <p class="media-card__prompt" :title="item.prompt">{{ item.prompt }}</p>
        <div class="connection-actions">
          <button class="button button--ghost button--small" type="button" @click="copy(item)"><Check v-if="copied === item.id" :size="14" /><Copy v-else :size="14" /> {{ copied === item.id ? 'Copied' : 'Copy link' }}</button>
          <a class="button button--ghost button--small" :href="item.url" target="_blank" rel="noopener noreferrer"><ExternalLink :size="14" /> Open</a>
        </div>
      </div>
    </article>
  </section>
  <section v-else class="detail-card detail-card--wide"><p>No media yet. Ask your Manager for social posts, ad creatives, or short videos, and they will appear here for review.</p></section>
</div></template>
